// ===========================================
// VOICE INPUT (push-to-talk → Whisper → text)
// ===========================================
// Tap to start listening, tap again to finish. The audio is transcribed
// server-side and handed to onTranscript — the same path as typing, so voice
// can do anything typing can. Everything fails calm and quiet: a bad take is
// a "try again", never an error screen.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';

export type VoiceState = 'idle' | 'recording' | 'transcribing';

export function isVoiceSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function useVoiceInput(onTranscript: (text: string) => void) {
  const [state, setState] = useState<VoiceState>('idle');
  const [notice, setNotice] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNotice = useCallback((text: string) => {
    setNotice(text);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 4000);
  }, []);

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
  }, []);

  const transcribe = useCallback(async (blob: Blob, mimeType: string) => {
    setState('transcribing');
    try {
      const token = useAuthStore.getState().token;
      const form = new FormData();
      const ext = mimeType.includes('webm') ? 'webm' : mimeType.includes('ogg') ? 'ogg' : 'm4a';
      form.append('file', blob, `voice.${ext}`);

      const res = await fetch(`${API_URL}/v1/ai/transcribe`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      const json = await res.json().catch(() => null);
      if (res.ok && json?.success && json.data?.text) {
        onTranscript(json.data.text);
      } else {
        showNotice(json?.error?.message || "Couldn't make that out — give it another try.");
      }
    } catch {
      showNotice("Couldn't reach the server — check the connection and try again.");
    } finally {
      setState('idle');
    }
  }, [onTranscript, showNotice]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  const start = useCallback(async () => {
    if (state !== 'idle') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Safari records mp4/m4a; Chrome records webm — Whisper takes both
      const mimeType = ['audio/mp4', 'audio/webm', 'audio/ogg'].find(
        (t) => typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(t)
      ) || '';

      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/mp4' });
        if (blob.size < 1000) {
          setState('idle');
          showNotice('That was too quick to catch — hold on a beat while you speak.');
          return;
        }
        void transcribe(blob, recorder.mimeType || 'audio/mp4');
      };

      recorder.start();
      setState('recording');
      setNotice(null);
      // Never record forever if someone walks away mid-thought
      maxTimerRef.current = setTimeout(() => stop(), 60000);
    } catch {
      cleanupStream();
      setState('idle');
      showNotice('Mise needs microphone access for this — you can allow it in Settings.');
    }
  }, [state, cleanupStream, transcribe, stop, showNotice]);

  const toggle = useCallback(() => {
    if (state === 'recording') stop();
    else if (state === 'idle') void start();
  }, [state, start, stop]);

  useEffect(() => () => {
    // Unmount: drop the mic and timers, ignore anything in flight
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* already stopped */ }
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  return { state, notice, toggle, isSupported: isVoiceSupported() };
}
